import * as React from "react";
import { cn } from "@/utils/cn";

interface TabsContextValue {
	value: string;
	onValueChange: (value: string) => void;
}

const TabsContext = React.createContext<TabsContextValue | undefined>(undefined);

interface TabsProps {
	defaultValue: string;
	value?: string;
	onValueChange?: (value: string) => void;
	className?: string;
	children: React.ReactNode;
}

export function Tabs({
	defaultValue,
	value: controlledValue,
	onValueChange,
	className,
	children,
}: TabsProps) {
	const [uncontrolledValue, setUncontrolledValue] = React.useState(defaultValue);
	const value = controlledValue ?? uncontrolledValue;

	const handleValueChange = React.useCallback(
		(newValue: string) => {
			setUncontrolledValue(newValue);
			onValueChange?.(newValue);
		},
		[onValueChange]
	);

	return (
		<TabsContext.Provider value={{ value, onValueChange: handleValueChange }}>
			<div className={cn("", className)}>{children}</div>
		</TabsContext.Provider>
	);
}

function useTabsContext() {
	const context = React.useContext(TabsContext);
	if (!context) {
		throw new Error("Tabs components must be used within a Tabs provider");
	}
	return context;
}

interface TabsListProps {
	className?: string;
	children: React.ReactNode;
}

export function TabsList({ className, children }: TabsListProps) {
	return (
		<div
			className={cn(
				"inline-flex h-10 items-center justify-center rounded-md bg-muted p-1 text-muted-foreground",
				className
			)}
		>
			{children}
		</div>
	);
}

interface TabsTriggerProps {
	value: string;
	className?: string;
	children: React.ReactNode;
	disabled?: boolean;
}

export function TabsTrigger({
	value,
	className,
	children,
	disabled,
}: TabsTriggerProps) {
	const { value: selectedValue, onValueChange } = useTabsContext();
	const isSelected = selectedValue === value;

	return (
		<button
			type="button"
			role="tab"
			aria-selected={isSelected}
			disabled={disabled}
			onClick={() => onValueChange(value)}
			className={cn(
				"inline-flex items-center justify-center whitespace-nowrap rounded-sm px-3 py-1.5 text-sm font-medium ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50",
				isSelected
					? "bg-background text-foreground shadow-sm"
					: "hover:bg-background/50 hover:text-foreground",
				className
			)}
		>
			{children}
		</button>
	);
}

interface TabsContentProps {
	value: string;
	className?: string;
	children: React.ReactNode;
}

export function TabsContent({ value, className, children }: TabsContentProps) {
	const { value: selectedValue } = useTabsContext();

	if (selectedValue !== value) {
		return null;
	}

	return (
		<div
			role="tabpanel"
			className={cn(
				"mt-2 ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
				className
			)}
		>
			{children}
		</div>
	);
}
